import * as React from 'react';
/**
 * @interface BadgeProps
 * @description Define las propiedades del componente Badge.
 */
interface BadgeProps {
  /**
   * @property {React.ReactNode} children - Los hijos del badge.
   */
  children: React.ReactNode;
  /**
   * @property {string} variant - La variante del badge.
   */
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  /**
   * @property {string} size - El tamaño del badge.
   */
  size?: 'sm' | 'md';
  /**
   * @property {boolean} solid - Fondo sólido (`-500`) con texto blanco, para pills de estatus. Va como prop y no
   * como `className` porque Tailwind ordena las utilidades de una misma propiedad por nombre: `bg-warning-500`
   * en `className` pierde contra el `bg-yellow-100` de la variante.
   */
  solid?: boolean;
  /**
   * @property {string} className - La clase del badge.
   */
  className?: string;
}

/**
 * @component Insignia
 * @description Un componente de insignia con diferentes variantes y tamaños.
 * @param {BadgeProps} props Las propiedades del componente.
 * @returns {React.ReactElement} El componente de insignia renderizado.
 */
export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'default',
  size = 'md',
  solid = false,
  className = '',
}) => {
  const variantClasses = {
    default: 'bg-gray-100 text-gray-800',
    success: 'bg-success-100 text-success-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-error-100 text-error-800',
    info: 'bg-blue-100 text-blue-800',
  };

  const solidClasses = {
    default: 'bg-gray-500 text-text-secondary',
    success: 'bg-success-500 text-text-secondary',
    warning: 'bg-warning-500 text-text-secondary',
    error: 'bg-error-500 text-text-secondary',
    info: 'bg-info-500 text-text-secondary',
  };

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-2.5 py-0.5 text-sm',
  };

  return (
    <span
      className={`inline-flex items-center font-medium rounded-full ${(solid ? solidClasses : variantClasses)[variant]} ${sizeClasses[size]} ${className}`}
    >
      {children}
    </span>
  );
};
